import React, { useEffect, useState } from "react";
import "./stats.css";
import CountUp from "react-countup";
import { shallowEqual, useSelector } from "react-redux";

const Tooltip = ({ x, y, content }) => {
  x = x + 20;
  y = y - 25;
  return (
    <div
      style={{
        position: "absolute",
        left: x,
        top: y,
        backgroundColor: "rgba(255, 255, 255, 0.85)",
        color: "#000",
        padding: "6px",
        paddingTop: "2px",
        borderRadius: "4px",
        pointerEvents: "none",
        zIndex: 9999,
        lineHeight: "1rem",

        // transition: 'all 0.1s ease-out',
      }}
    >
      <span style={{ fontSize: "0.6rem" }}>{content[1]}</span>
      <br />
      <span style={{ fontSize: "0.9rem" }}>
        <span style={{ color: "#FD953A", fontWeight: "700" }}>
          {content[0].toFixed(2)}KW
        </span>{" "}
        ({content[2].toPrecision(2)}%)
      </span>
    </div>
  );
};

function Stats() {
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    content: "",
  });

  const handleMouseOver = (event, content) => {
    const { clientX, clientY } = event;
    setTooltip({ visible: true, x: clientX + 0, y: clientY - 0, content });
  };

  const handleMouseMove = (event) => {
    const { clientX, clientY } = event;
    if (tooltip.visible) {
      setTooltip((prevTooltip) => ({
        ...prevTooltip,
        x: clientX + 0,
        y: clientY - 0,
      }));
    }
  };

  const handleMouseOut = () => {
    setTooltip({ visible: false, x: 0, y: 0, content: "" });
  };

  const statusCapacity = useSelector((state) => state.status, shallowEqual);

  const [statsData, setStatsData] = useState({
    Draft: { quantity: 0, capacity: 0 },
    Completed: { quantity: 0, capacity: 0 },
    "In Progress": { quantity: 0, capacity: 0 },
    Pending: { quantity: 0, capacity: 0 },
    fetched: false,
  });

  useEffect(() => {
    if (statusCapacity !== undefined) setStatsData(statusCapacity);
  }, [statusCapacity]);

  // Getting CSS Variables to manipulate styles
  let cssRoot = document.querySelector(":root");

  // Right Stat KW
  let rightStatKw = [
    statsData?.Completed?.quantity,
    statsData?.Pending?.quantity +
      statsData["In Progress"]?.quantity +
      statsData?.Draft?.quantity,
  ];
  // Left Stat KW Values
  let leftStatKw = [
    statsData?.Completed?.capacity,
    statsData?.Pending?.capacity + statsData["In Progress"]?.capacity,
    statsData?.Draft?.capacity,
  ];
  // let leftStatKw = [0, 0, 0];

  let leftStatTotal = leftStatKw[0] + leftStatKw[1] + leftStatKw[2];

  let rightPercentages = [
    (100 * rightStatKw[0]) / (rightStatKw[0] + rightStatKw[1]),
    (100 * rightStatKw[1]) / (rightStatKw[0] + rightStatKw[1]),
  ];
  let per = rightPercentages[0];

  let strokeDasharray = "";

  for (let i = 0; i < per; i++) {
    strokeDasharray += "2.5133 2.5133 ";
  }
  strokeDasharray += "0 10000";

  // console.log("strokeDasharray", strokeDasharray);

  // Setting Right Stat Animation End Points
  cssRoot.style.setProperty("--anim5", strokeDasharray);

  // Left Stat KW Values
  // Calculating Percentages
  let percentages = [
    (100 * leftStatKw[0]) / leftStatTotal,
    (100 * leftStatKw[1]) / leftStatTotal,
    (100 * leftStatKw[2]) / leftStatTotal,
  ];
  // let percentages = [100, 87.73, 20.27];
  // console.log("percentages", percentages);
  let strokeWidth = 15;
  let radii = [80, 62.5, 45];
  let [cx, cy] = [90, 90];
  let circumferences = radii.map((val) => val * 2 * Math.PI);

  let strokeLength = circumferences.map(
    (cir, index) => (cir * percentages[index]) / 100
  );
  // console.log("circumferences", circumferences);
  // console.log("strokeLength", strokeLength);
  // Setting Animation End Points
  cssRoot.style.setProperty(
    "--anim1",
    isNaN(strokeLength[0]) ? 0 : strokeLength[0]
  );
  cssRoot.style.setProperty(
    "--anim2",
    isNaN(strokeLength[1]) ? 0 : strokeLength[1]
  );
  cssRoot.style.setProperty(
    "--anim3",
    isNaN(strokeLength[2]) ? 0 : strokeLength[2]
  );
  return (
    <div className="stat-container">
      {/* ToolTip positioning element */}
      {tooltip.visible && (
        <Tooltip x={tooltip.x} y={tooltip.y} content={tooltip.content} />
      )}
      {/* Stat 1 */}
      <div className="left-stat-container">
        <div className="stat-bg"></div>
        {/* Stat 1 chart */}
        <svg className="stat-1" height="180" width="180">
          <circle
            cx={cx}
            cy={cy}
            r={radii[0]}
            fill="none"
            stroke="rgba(0,0,0,0.05)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          <circle
            onMouseOver={(event) =>
              handleMouseOver(event, [
                leftStatKw[0],
                "Total systems installed",
                percentages[0],
              ])
            }
            onMouseMove={handleMouseMove}
            onMouseOut={handleMouseOut}
            className="rotation scale anim-1"
            cx={cx}
            cy={cy}
            r={radii[0]}
            fill="none"
            stroke="#27326A"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          <circle
            cx={cx}
            cy={cy}
            r={radii[1]}
            fill="none"
            stroke="rgba(0,0,0,0.05)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          <circle
            onMouseOver={(event) =>
              handleMouseOver(event, [
                leftStatKw[1],
                "Total systems in progress",
                percentages[1],
              ])
            }
            onMouseMove={handleMouseMove}
            onMouseOut={handleMouseOut}
            className="rotation scale anim-2"
            cx={cx}
            cy={cy}
            r={radii[1]}
            fill="none"
            stroke="#60B8EE"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${strokeLength[1]},${circumferences[1]}`}
          />
          <circle
            cx={cx}
            cy={cy}
            r={radii[2]}
            fill="none"
            stroke="rgba(0,0,0,0.05)"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          <circle
            onMouseOver={(event) =>
              handleMouseOver(event, [
                leftStatKw[2],
                "Total systems in queue",
                percentages[2],
              ])
            }
            onMouseMove={handleMouseMove}
            onMouseOut={handleMouseOut}
            className="rotation scale anim-3"
            cx={cx}
            cy={cy}
            r={radii[2]}
            fill="none"
            stroke="#B7DCFF"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${strokeLength[2]},${circumferences[2]}`}
          />
          <text className="svg-text-up" x="67" y="89">
            Yield
          </text>
          <text className="svg-text-down" x="80" y="105">
            KW
          </text>
        </svg>

        {/* Stat 1 text */}
        <div className="left-text-container">
          {/* one */}
          <div className="stat-info">
            <svg height="34" width="34">
              <circle
                cx={17}
                cy={17}
                r={13}
                fill="none"
                stroke="#27326A"
                strokeWidth={8}
              />
            </svg>
            {/* <div className='ball color1'></div> */}
            <div className="ball-text">
              <span className="value">
                <CountUp
                  start={0.0}
                  end={leftStatKw[0]}
                  duration={2}
                  delay={1}
                  separator=" "
                  decimals={2}
                />
                kW
              </span>
              <br />
              <span className="normal">Total systems installed - </span>
              <span className="percent">
                <CountUp
                  start={0}
                  end={percentages[0]}
                  duration={2}
                  delay={1}
                  separator=" "
                  decimals={2}
                />
                %
              </span>
            </div>
          </div>
          {/* two */}
          <div className="stat-info">
            <svg height="34" width="34">
              <circle
                cx={17}
                cy={17}
                r={13}
                fill="none"
                stroke="#60B8EE"
                strokeWidth={8}
              />
            </svg>
            {/* <div className='ball color2'></div> */}
            <div className="ball-text">
              <span className="value">
                <CountUp
                  start={0}
                  end={leftStatKw[1]}
                  duration={1.5}
                  delay={1}
                  separator=" "
                  decimals={2}
                />
                kW
              </span>
              <br />
              <span className="normal">Total systems in progress - </span>
              <span className="percent">
                <CountUp
                  start={0}
                  end={percentages[1]}
                  duration={1.5}
                  delay={1}
                  separator=" "
                  decimals={2}
                />
                %
              </span>
            </div>
          </div>
          {/* three */}
          <div className="stat-info">
            <svg height="34" width="34">
              <circle
                cx={17}
                cy={17}
                r={13}
                fill="none"
                stroke="#B7DCFF"
                strokeWidth={8}
              />
            </svg>
            {/* <div className='ball color3'></div> */}
            <div className="ball-text">
              <span className="value">
                <CountUp
                  start={0}
                  end={leftStatKw[2]}
                  duration={1}
                  delay={1}
                  separator=" "
                  decimals={2}
                />{" "}
                kW
              </span>
              <br />
              <span className="normal">Total systems in queue - </span>
              <span className="percent">
                <CountUp
                  start={0}
                  end={percentages[2]}
                  duration={1}
                  delay={1}
                  separator=" "
                  decimals={2}
                />
                %
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Stat 2 */}
      <div className="right-stat-container">
        <div className="stat-bg"></div>
        <div className="one-text left">
          <div className="a">
            <span className="b">
              <CountUp
                start={0}
                end={statsData?.Completed?.quantity}
                duration={2}
                delay={1}
                separator=" "
                decimals={0}
              />
            </span>
            <br />
            No. of projects completed
          </div>
          <div className="c">
            (
            <CountUp
              start={0.0}
              end={rightPercentages[0]}
              duration={2}
              delay={1}
              separator=" "
              decimals={2}
            />
            %)
          </div>
        </div>
        <svg height="180" width="180">
          <circle
            className="anim-4"
            cx="90"
            cy="90"
            r="80"
            stroke="rgba(200,200,200,0.8)"
            strokeWidth="15"
            fill="none"
            strokeDasharray="2.5133 2.5133 "
          />
          <circle
            className="rotation2 anim-5"
            cx="90"
            cy="90"
            r="80"
            stroke="#FF7C1B"
            strokeWidth="15"
            fill="none"
            strokeDasharray={strokeDasharray}
          />
          <circle
            className="rotation2"
            cx={cx}
            cy={cy}
            r="63"
            fill="none"
            stroke="#CDD4D988"
            strokeWidth="2"
          />
          <text className="svg-text" x="37" y="97">
            Consumption
          </text>
          Sorry, your browser does not support inline SVG.
        </svg>

        <div className="one-text right">
          <div className="a">
            <span className="b">
              <CountUp
                start={0}
                end={
                  statsData?.Draft?.quantity +
                  statsData["In Progress"]?.quantity +
                  statsData?.Pending?.quantity
                }
                duration={2}
                delay={1}
                separator=" "
                decimals={0}
              />
            </span>{" "}
            <br />
            No. of projects in progress
          </div>
          <div className="c">
            (
            <CountUp
              start={0.0}
              end={rightPercentages[1]}
              duration={2}
              delay={1}
              separator=" "
              decimals={2}
            />
            %)
          </div>
        </div>
      </div>
    </div>
  );
}

export default Stats;
